import i18n from 'i18next';
import ICU from 'i18next-icu';
import { initReactI18next } from 'react-i18next';
import { setFormatLocale, setUiStrings } from '@finokay/ui';
import de from '../i18n/de.json';
import en from '../i18n/en.json';

/**
 * English is the base; every other language is a locale like any other.
 * The language comes from the user's setting, before login from the
 * browser. Keys only, never literal strings in components.
 */
export const LANGUAGES = ['en', 'de'] as const;
export type Language = (typeof LANGUAGES)[number];

/** A BCP 47 tag the app supports, from a user setting or the browser: "de-AT" stays, "fr-FR" becomes "en". */
export function supportedLocale(tag: string | undefined | null): string {
  const lang = tag?.split('-')[0]?.toLowerCase();
  return lang !== undefined && (LANGUAGES as readonly string[]).includes(lang) ? tag! : 'en';
}

/**
 * Numbers and dates follow the person, not the language of the text: their
 * setting when it names a region ("en-US", "de-AT"), otherwise their
 * browser's ("en" + browser "en-AU" → Australian dates; an untranslated
 * "fr-FR" reads English text with French numbers and dates).
 */
export function formatLocaleFor(locale: string, browser = browserLocale()): string {
  if (locale.includes('-')) return locale;
  return browser ?? locale;
}

function browserLocale(): string | undefined {
  if (typeof navigator === 'undefined') return undefined;
  return navigator.languages?.[0] ?? navigator.language;
}

function applyLocale(locale: string) {
  setFormatLocale(formatLocaleFor(locale));
  setUiStrings({
    cancel: i18n.t('common.cancel'),
    close: i18n.t('common.close'),
    loading: i18n.t('common.loading'),
    noEntries: i18n.t('common.noEntries'),
    account: i18n.t('common.account'),
    accountSearch: i18n.t('common.accountSearch'),
    noAccount: i18n.t('common.noAccount')
  });
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
}

i18n.on('languageChanged', applyLocale);

void i18n
  .use(ICU)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en }, de: { translation: de } },
    lng: supportedLocale(typeof navigator === 'undefined' ? undefined : navigator.language),
    supportedLngs: [...LANGUAGES],
    nonExplicitSupportedLngs: true,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    returnNull: false
  });

export default i18n;
